// pages/ConfirmacaoPagamento.js
"use client"

import { useEffect, useState, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { motion } from "framer-motion"
import { ShoppingBag, AlertCircle, FileText } from "lucide-react"
import { FaSmile, FaFrown } from "react-icons/fa"
import axios from "axios"
import "../styles/confirmacaoPagamento.css"
import check from "../assets/check.png"
import jsPDF from "jspdf"
import "jspdf-autotable"
import JsBarcode from "jsbarcode"
import logo from "../assets/2.png"
import nadaImg from "../assets/nada.jpg" // ajuste o caminho/nome se necessário
const DEFAULT_NADA_IMG = nadaImg

const API = "https://api.ticketdovaqueiro.com/api"

const ConfirmacaoPagamento = () => {
  const navigate = useNavigate()
  const { pedidoId } = useParams()

  const [pedido, setPedido] = useState(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState("")

  // Raspadinha
  const [entries, setEntries] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [carregandoEntries, setCarregandoEntries] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(null)
  const currentEntry = currentIndex !== null ? entries[currentIndex] : null

  // Segura o fechamento por 30s após raspagem da última cota
  const closeHoldRef = useRef(0)
  const holdTimeoutRef = useRef(null)

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

  const updateModalFromList = (lista) => {
    const temDisponivel = lista.some((e) => !e.scratched)
    const now = Date.now()
    if (temDisponivel) {
      setShowModal(true)
    } else {
      if (now >= closeHoldRef.current) setShowModal(false)
      else setShowModal(true)
    }
  }

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/raspadinha/entries-by-pedido/${pedidoId}`)
      const lista = Array.isArray(res.data) ? res.data : []
      lista.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      setEntries(lista)
      updateModalFromList(lista)
      return lista
    } catch (e) {
      console.error("[RASPADINHA] erro ao refazer GET entries:", e?.message)
      return []
    }
  }

  useEffect(() => {
    if (!pedidoId) {
      setErro("Pedido não encontrado!")
      setLoading(false)
      return
    }
    let cancelado = false
    let timerPedido = null

    const buscarEntriesComRetry = async (tentativas = 8, intervaloMs = 1500) => {
      setCarregandoEntries(true)
      for (let i = 1; i <= tentativas; i++) {
        const lista = await fetchEntries()
        if (lista.length > 0) {
          setCarregandoEntries(false)
          return true
        }
        await sleep(intervaloMs)
      }
      setCarregandoEntries(false)
      return false
    }

    const buscarPedido = async () => {
      try {
        const response = await axios.get(`${API}/pedidos/${pedidoId}`)
        if (cancelado) return clearInterval(timerPedido)
        const p = response.data
        setPedido(p)

        if (p?.statusPagamento === "pago") {
          await buscarEntriesComRetry()
        } else {
          let ciclos = 0
          timerPedido = setInterval(async () => {
            if (cancelado) return clearInterval(timerPedido)
            ciclos++
            if (ciclos > 10) {
              clearInterval(timerPedido)
              return
            }
            try {
              const r = await axios.get(`${API}/pedidos/${pedidoId}`)
              const ped = r.data
              setPedido(ped)
              if (ped?.statusPagamento === "pago") {
                clearInterval(timerPedido)
                await buscarEntriesComRetry()
              }
            } catch {}
          }, 2000)
        }
      } catch (error) {
        console.error("❌ Erro ao buscar pedido:", error)
        setErro("Erro ao carregar os detalhes do pedido.")
      } finally {
        if (!cancelado) setLoading(false)
      }
    }

    buscarPedido()
    return () => {
      cancelado = true
      if (timerPedido) clearInterval(timerPedido)
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current)
        holdTimeoutRef.current = null
      }
    }
  }, [pedidoId])

  const isWinner = (entry) => !!entry?.isWinner;

  const labelDoBotao = (entry) => {
    if (!entry?.scratched) return "RASPE AQUI"
    return isWinner(entry) ? "GANHOU" : "PERDEU"
  }

  const abrirRaspadinha = (idx) => setCurrentIndex(idx)

  const marcarRaspada = async (entryId) => {
    try {
      const restantesAntes = entries.filter((e) => !e.scratched).length
      const estaEraUltima = restantesAntes <= 1

      if (estaEraUltima) {
        closeHoldRef.current = Date.now() + 30_000
        setShowModal(true)
        if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current)
        holdTimeoutRef.current = setTimeout(() => {
          if (Date.now() >= closeHoldRef.current) setShowModal(false)
          holdTimeoutRef.current = null
        }, 30_000)
      }

      setEntries((prev) => prev.map((e) => (e._id === entryId ? { ...e, scratched: true } : e)))
      await axios.patch(`${API}/raspadinha/entry/${entryId}/scratched`)
      await fetchEntries()
    } catch (e) {
      console.error("[RASPADINHA] erro marcar raspada:", e?.response?.status, e?.message)
    }
  }

  /* ======= PDF (mantido) ======= */
  const gerarPDF = (pedido) => {
    const doc = new jsPDF("p", "mm", "a4")
    const pageWidth = doc.internal.pageSize.getWidth()

    const img = new Image()
    img.src = logo
    img.crossOrigin = "anonymous"

    const nomeEvento = (pedido.evento?.nome || "").toUpperCase().replace(" DO ", "\nDO")
    const nomeEventoLinhas = nomeEvento.split("\n")

    const codigoAutenticacao = pedido._id.slice(-6).toUpperCase() + "-" + Math.floor(100000 + Math.random() * 900000)

    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.text("Autenticidade do Pedido", pageWidth / 2, 272, { align: "center" })
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.text(`Código: ${codigoAutenticacao}`, pageWidth / 2, 278, { align: "center" })

    const canvas = document.createElement("canvas")
    JsBarcode(canvas, codigoAutenticacao, { format: "CODE128", width: 1.5, height: 10, displayValue: false, margin: 0 })
    const barcodeImage = canvas.toDataURL("image/png")
    doc.addImage(barcodeImage, "PNG", (pageWidth - 80) / 2, 280, 80, 15)

    img.onload = () => {
      doc.addImage(img, "PNG", 75, 5, 45, 20)
      doc.setFont("helvetica", "bold")
      doc.setFontSize(16)
      doc.text(nomeEventoLinhas, pageWidth / 2, 30, { align: "center" })

      doc.setFillColor("#FFD700")
      doc.rect(10, 43, pageWidth - 20, 10, "F")
      doc.setFont("helvetica", "bold")
      doc.setFontSize(14)
      doc.text("DADOS DO VAQUEIRO", pageWidth / 2, 50, { align: "center" })

      doc.setFont("helvetica", "bold")
      doc.setFontSize(11)
      doc.text("NOME DO VAQUEIRO:", 15, 60)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.nomeVaqueiro || "Não informado", 58, 60)

      doc.setFont("helvetica", "bold")
      doc.text("CPF:", 155, 60)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.cpf || "Não informado", 165, 60)

      doc.setFont("helvetica", "bold")
      doc.text("NOME DO ESTEIRA:", 15, 68)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.nomeEsteira || "Não informado", 55, 68)

      doc.setFont("helvetica", "bold")
      doc.text("CAVALO DE PUXADA:", 15, 76)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.cavaloPuxar || "Não informado", 58, 76)

      doc.setFont("helvetica", "bold")
      doc.text("CAVALO DE ESTEIRA:", 15, 84)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.cavaloEsteira || "Não informado", 58, 84)

      doc.setFont("helvetica", "bold")
      doc.text("REPRESENTAÇÃO:", 15, 92)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.representacao || "Não informado", 53, 92)

      doc.setFont("helvetica", "bold")
      doc.text("CIDADE:", 15, 100)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.cidade || "Não informado", 33, 100)

      doc.setFillColor("#FFD700")
      doc.rect(10, 108, pageWidth - 20, 10, "F")
      doc.setFont("helvetica", "bold")
      doc.setFontSize(14)
      doc.text("INFORMAÇÕES DO PEDIDO", pageWidth / 2, 116, { align: "center" })

      doc.setFontSize(11)
      doc.setFont("helvetica", "bold")
      doc.text("CATEGORIA:", 15, 125)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.categoria || "-", 40, 125)

      doc.setFont("helvetica", "bold")
      doc.text("FORMA DE PAGAMENTO:", 15, 132)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.metodoPagamento || "-", 65, 132)

      doc.setFont("helvetica", "bold")
      doc.text("DATA DA COMPRA:", 15, 139)
      const dataCompra = new Date(pedido.dataCriacao)
        .toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
        .replace(",", " às")
        .replace(":", "H")
      doc.setFont("helvetica", "normal")
      doc.text(dataCompra, 53, 139)

      doc.setFont("helvetica", "bold")
      doc.text("STATUS DO PAGAMENTO:", 118, 125)
      doc.setFont("helvetica", "normal")
      doc.text(pedido.statusPagamento?.toUpperCase() || "-", 170, 125)

      doc.setFont("helvetica", "bold")
      doc.text("TOTAL:", 125, 132)
      doc.setFont("helvetica", "normal")
      doc.text(`R$ ${Number(pedido.total).toFixed(2).replace(".", ",")}`, 140, 132)

      doc.setFillColor("#FFD700")
      doc.rect(10, 147, pageWidth - 20, 10, "F")
      doc.setFont("helvetica", "bold")
      doc.setFontSize(14)
      doc.text("SENHAS ADQUIRIDAS", pageWidth / 2, 155, { align: "center" })

      const colunasPorLinha = 4
      const larguraColuna = 45
      const altura = 25
      const espacamento = 5
      const startY = 165

      pedido.senhas.forEach((senha, i) => {
        const coluna = i % colunasPorLinha
        const linha = Math.floor(i / colunasPorLinha)
        const x = 15 + coluna * (larguraColuna + espacamento)
        const y = startY + linha * (altura + 10)

        doc.setFillColor(255, 236, 93)
        doc.roundedRect(x, y, larguraColuna, altura, 3, 3, "F")
        doc.setTextColor(0, 0, 0)
        doc.setFont("helvetica", "bold")
        doc.setFontSize(10)
        doc.text(`SENHA ${i + 1}`, x + 14, y + 6)
        doc.setFontSize(30)
        doc.text(`${senha.numero}`, x + 16, y + 15)
        doc.setFontSize(10)
        doc.setFont("helvetica", "normal")
        doc.text(`BOI TV: ${senha.boiTv ? "SIM" : "NÃO"}`, x + 12, y + 20)
      })

      doc.save(`Pedido_${pedido._id}.pdf`)
    }
  }

  const MiniCard = ({ entry, idx, onOpen }) => {
    const status = labelDoBotao(entry)
    const ganhou = isWinner(entry)

    const handleClick = () => {
      if (entry.scratched && !ganhou) return
      onOpen(idx)
    }

    return (
      <button
        className={`rasp-btn ${entry.scratched ? (ganhou ? "won" : "lose") : ""}`}
        onClick={handleClick}
        aria-label={`Raspadinha ${idx + 1}`}
        style={{ "--index": idx }}
      >
        <span className="rasp-btn-title">
          {entry.scratched ? ganhou ? <FaSmile /> : <FaFrown /> : "🎯"} {status}
        </span>
        <span className="rasp-btn-sub">#{idx + 1}</span>
      </button>
    )
  }

  const FireworksExplosion = ({ isVisible, onComplete }) => {
    useEffect(() => {
      if (isVisible) {
        const timer = setTimeout(() => {
          onComplete?.()
        }, 2000)
        return () => clearTimeout(timer)
      }
    }, [isVisible, onComplete])

    if (!isVisible) return null

    return (
      <div className="fireworks-explosion">
        {/* Explosão central */}
        <div className="explosion-center">
          {Array.from({ length: 40 }).map((_, i) => (
            <div
              key={`center-${i}`}
              className="explosion-particle"
              style={{
                "--angle": `${(360 / 40) * i}deg`,
                "--distance": `${120 + Math.random() * 80}px`,
                "--delay": `${Math.random() * 0.3}s`,
                "--duration": `${1.2 + Math.random() * 0.8}s`,
              }}
            />
          ))}
        </div>

        {/* Confetes caindo */}
        {Array.from({ length: 60 }).map((_, i) => (
          <div
            key={`confetti-${i}`}
            className="confetti-piece"
            style={{
              "--x": `${Math.random() * 100}vw`,
              "--delay": `${Math.random() * 1.5}s`,
              "--duration": `${2 + Math.random() * 2}s`,
              "--rotation": `${Math.random() * 360}deg`,
              "--color": `hsl(${[45, 60, 120, 200, 280, 320][Math.floor(Math.random() * 6)]}, 70%, 60%)`,
            }}
          />
        ))}

        {/* Estrelas brilhantes */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={`star-${i}`}
            className="star-sparkle"
            style={{
              "--x": `${20 + Math.random() * 60}%`,
              "--y": `${20 + Math.random() * 60}%`,
              "--delay": `${Math.random() * 1}s`,
              "--scale": `${0.5 + Math.random() * 1}`,
            }}
          />
        ))}
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bb-confirmacao-container"
    >
      {loading ? (
        <div className="bb-confirmacao-loading">
          <div className="bb-loading-spinner"></div>
          <p>Carregando detalhes do pedido...</p>
        </div>
      ) : erro ? (
        <div className="bb-erro">
          <AlertCircle size={24} />
          <p>{erro}</p>
        </div>
      ) : (
        <>
          <motion.img
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            src={check}
            alt="check"
            className="bb-confirmacao-container-img"
          />
          <motion.h2
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Pagamento Aprovado!
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bb-confirmacao-detalhes"
          >
            <div className="bb-confirmacao-item">
              <span>Pedido ID:</span>
              <span>{pedido?._id}</span>
            </div>
            <div className="bb-confirmacao-item">
              <span>Valor Total Pago:</span>
              <span>R$ {pedido?.total?.toFixed(2)}</span>
            </div>
            <div className="bb-confirmacao-item">
              <span>Método de Pagamento:</span>
              <span>{pedido?.metodoPagamento?.toUpperCase?.() || "-"}</span>
            </div>
            <div className="bb-confirmacao-item">
              <span>Data do Pagamento:</span>
              <span>{new Date().toLocaleDateString("pt-BR")}</span>
            </div>
          </motion.div>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/meus-pedidos")}
          >
            <ShoppingBag size={18} /> Ver meus pedidos
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => gerarPDF(pedido)}
            className="bb-btn-home"
          >
            <FileText size={18} /> Ver comprovante (PDF)
          </motion.button>

          {showModal && (
            <div className="modalRaspadinha">
              <div className="raspclose-area">
                <button onClick={() => setShowModal(false)} className="raspclose-x" aria-label="Fechar" type="button">
                  ×
                </button>
              </div>

              <div className="modal-inner">
                <div className="modal-title">
                  <span className="tech-icon">⚡</span>
                  SISTEMA DE RASPADINHAS
                  <span className="tech-badge">v2.0</span>
                </div>

                {carregandoEntries && entries.length === 0 && (
                  <div className="bb-confirmacao-loading tech-loading">
                    <div className="tech-spinner"></div>
                    <p>Inicializando sistema de prêmios...</p>
                  </div>
                )}

                <div className="rasp-header">
                  <div className="rasp-qty tech-counter">
                    <span className="counter-label">DISPONÍVEIS</span>
                    <span className="counter-value">{entries.filter((e) => !e.scratched).length}</span>
                    <span className="counter-total">/ {entries.length}</span>
                  </div>
                </div>

                {entries.length > 0 && (
                  <div className="rasp-cta-grid">
                    {entries.map((entry, idx) => (
                      <MiniCard key={entry._id} entry={entry} idx={idx} onOpen={setCurrentIndex} />
                    ))}
                  </div>
                )}

                {currentEntry && (
                  <>
                    <BigScratch
                      key={currentEntry._id}
                      entry={currentEntry}
                      onScratchEnd={async () => {
                        await marcarRaspada(currentEntry._id)
                      }}
                      isWinnerFlag={isWinner(currentEntry)}
                    />

                    {/* ✅ Só mostra confete + mensagem DEPOIS de raspada e se for vencedora */}
                    {currentEntry.scratched && isWinner(currentEntry) && (
                      <>
                        <div className="win-info tech-notification">
                          <span className="notification-icon">🎯</span>
                          Nossa equipe irá entrar em contato com você.
                        </div>
                        <FireworksExplosion
                          isVisible={true}
                          onComplete={() => console.log("Explosão finalizada")}
                        />
                      </>
                    )}
                  </>
                )}

              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  )
}

/* ===========================
 * Canvas da raspadinha (overlay só das listras)
 * =========================== */
function BigScratch({ entry, onScratchEnd, isWinnerFlag }) {
  const canvasRef = useRef(null)
  const ctxRef = useRef(null)
  const [scratchedLocal, setScratchedLocal] = useState(entry?.scratched || false)

  const paintOverlay = (ctx, w, h) => {
    ctx.save()
    ctx.globalCompositeOperation = "source-over"

    // base metálica neutra (cinza)
    const g = ctx.createLinearGradient(0, 0, w, h)
    g.addColorStop(0, "#c9cacb")
    g.addColorStop(0.5, "#d7d8da")
    g.addColorStop(1, "#c0c1c3")
    ctx.fillStyle = g
    ctx.fillRect(0, 0, w, h)

    // listras diagonais discretas
    ctx.strokeStyle = "rgba(0,0,0,0.15)"
    ctx.lineWidth = 2
    const gap = 14
    for (let x = -h; x < w + h; x += gap) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x + h, h)
      ctx.stroke()
    }

    // texto “RASPE AQUI”
    ctx.fillStyle = "rgba(255,255,255,0.96)"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    const fontSize = Math.floor(w * 0.1)
    ctx.font = `900 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Arial`
    for (let i = 1; i <= 3; i++) ctx.fillText("RASPE AQUI", w / 2, (h / 4) * i)

    // habilita “borracha”
    ctx.restore()
    ctx.globalCompositeOperation = "destination-out"
  }

  useEffect(() => {
    setScratchedLocal(!!entry?.scratched)
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    ctxRef.current = ctx

    const size = Math.min(380, Math.floor(window.innerWidth * 0.92))
    canvas.width = size
    canvas.height = size

    paintOverlay(ctx, canvas.width, canvas.height)

    const prevent = (e) => e.preventDefault()
    canvas.addEventListener("touchstart", prevent, { passive: false })
    canvas.addEventListener("touchmove", prevent, { passive: false })

    return () => {
      canvas.removeEventListener("touchstart", prevent)
      canvas.removeEventListener("touchmove", prevent)
    }
  }, [entry])

  function handleScratch(e) {
    if (!ctxRef.current || scratchedLocal || entry?.scratched) return

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    const x = clientX - rect.left
    const y = clientY - rect.top

    ctxRef.current.beginPath()
    ctxRef.current.arc(x, y, 22, 0, Math.PI * 2)
    ctxRef.current.fill()

    // quanto já foi “apagado”
    const imageData = ctxRef.current.getImageData(0, 0, canvas.width, canvas.height)
    let cleared = 0
    for (let i = 3; i < imageData.data.length; i += 4) if (imageData.data[i] === 0) cleared++
    const percent = (cleared / (canvas.width * canvas.height)) * 100

    if (percent > 55 && !scratchedLocal) {
      setScratchedLocal(true)
      onScratchEnd?.()
    }
  }

  return (
    <div className="big-scratch-container">
      <div className="big-scratch-card">
        {/* faces embaixo; o canvas fica por cima e “raspa” */}
        <div className="big-faces">
          {entry.faces.map((face, idx) => {
            const key = String(face || "").toUpperCase()
            const isNada = key === "NADA"

            // 1) imagem vinda do backend via mapa (quando existir)
            const imgFromMap = entry?.faceImageMap?.[key] || ""

            // 2) fallback para o prêmio vencedor desta entry (comportamento antigo)
            const winnerImgFallback =
              !isNada && entry?.imagemPremioUrl && String(entry?.nomePremio || "").toUpperCase() === key
                ? entry.imagemPremioUrl
                : ""

            // 3) fallback de "NADA" (imagem padrão)
            const nadaImgFallback = isNada ? DEFAULT_NADA_IMG : ""

            // ordem de prioridade: mapa -> vencedor -> nada-padrão
            const img = imgFromMap || winnerImgFallback || nadaImgFallback

            return (
              <div key={idx} className="big-face">
                {img ? (
                  <img
                    src={img || "/placeholder.svg"}
                    alt={key}
                    className="scratch-face-img"
                    style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 8 }}
                  />
                ) : (
                  <span className="scratch-face-text">{key}</span> // caso ainda não haja imagem
                )}
              </div>
            )
          })}
        </div>

        {!(entry.scratched || scratchedLocal) && (
          <canvas
            ref={canvasRef}
            onMouseMove={handleScratch}
            onTouchMove={handleScratch}
            className="big-scratch-canvas"
            aria-label="Raspe aqui"
          />
        )}

        {(entry.scratched || scratchedLocal) && isWinnerFlag && (
          <>
            <div className="win-banner">
              <div className="win-title">🎉 PARABÉNS! VOCÊ GANHOU!</div>
              <div className="win-prize">{entry?.nomePremio || "Prêmio"}</div>
            </div>
            <div className="fireworks">
              {Array.from({ length: 24 }).map((_, i) => (
                <span
                  key={i}
                  className="spark"
                  style={{
                    "--dx": `${(Math.random() - 0.5) * 360}px`,
                    "--dy": `${(Math.random() - 0.5) * 360}px`,
                    animationDelay: `${Math.random() * 0.4}s`,
                  }}
                />
              ))}
            </div>
          </>
        )}

        {(entry.scratched || scratchedLocal) && !isWinnerFlag && (
          <div className="lose-banner">
            <div className="lose-title">😕 NÃO FOI DESSA VEZ</div>
            <div className="lose-hint">Tente na próxima</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConfirmacaoPagamento
